/**
 * PipelineFlow — FEAT-MONITOR-002 (Message lifecycle, machine-lane view).
 * Renders the Message Monitor diagram as ONE box per real VM / Redis / database (no duplication);
 * worker-stages are rows inside each VM; smooth connectors dock at the exact row (port).
 *
 * This file owns ONLY the diagram (layout + how data flows). Every metric is read straight from the
 * live `pipeline` payload (the standalone admin-monitor service) — nodes are looked up by their
 * backend id and edge rates by their flow pair — so the monitoring/metrics stay exactly as-is:
 *   machine  → pipeline node {cpu_pct, ram_pct, disk_pct, status, connections, servers[]}
 *   redis    → pipeline node {mem_pct, mem_used_mb, mem_free_mb, reads_per_sec, writes_per_sec, queues[]}
 *   store    → pipeline node {connections_active, connections, resident_mb, reads_per_sec, writes_per_sec}
 *   edge tps → pipeline flow {from, to, tps}
 * A role with no agent renders DISABLED ("no active monitor"), never hidden. No TPS → no animation.
 */
import React, { useMemo, useRef, useState, useEffect } from "react";
import { Box, Flex, HStack, Text, Icon, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Table, Thead, Tbody, Tr, Th, Td, Divider } from "@chakra-ui/react";
import { WarningTwoIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { TONE, fmtInt } from "./helpers";

const BASE_W = 1480, H = 792, FLOW = "#6d7cff";
const tone = (v, hi, mid) => (v == null ? "#94A3B8" : v >= hi ? "#DC2626" : v >= mid ? "#EA580C" : "#16A34A");
const cpuC = (v) => tone(v, 85, 60), ramC = (v) => tone(v, 90, 75);
const fmtMB = (mb) => (mb == null ? "—" : mb >= 1024 ? `${(mb / 1024).toFixed(1)}gb` : `${Math.round(mb)}mb`);
const fmtRate = (n) => (n == null ? "—" : `${Number(n).toLocaleString("en-US", { maximumFractionDigits: 1 })}/s`);

// ── Machines (VMs): backend `node` carries CPU/RAM/Disk + drill-down servers; rows are worker-
//    stages, each pulling its rate from a backend flow pair. ─────────────────────────────────────
const HEADER_H = 64;
const MACHINES = {
  incoming: { x: 18, y: 70, w: 236, h: 128, name: "Incoming API", node: "incoming_api",
    rows: [{ id: "accept", label: "incoming-message", sub: "/v2/send · /v2/save", flow: ["incoming_api", "incoming_redis"] }] },
  rrs: { x: 572, y: 66, w: 268, h: 252, name: "Right Route Server", node: "right_route",
    rows: [
      { id: "dispatch", label: "send-message-job", sub: "dispatch → providers", flow: ["right_route_redis", "channel"] },
      { id: "validate", label: "customer-validate", sub: "validate → writes Mongo", hot: true, flow: ["incoming_redis", "right_route"] },
      { id: "campaign", label: "campaign-message-job", sub: "campaign → send-msg-process", flow: ["campaign_engine", "campaign_queue"] }] },
  listener: { x: 1212, y: 268, w: 252, h: 188, name: "Webhook Listener", node: "listener",
    rows: [
      { id: "listen", label: "listener", sub: "DLR intake · /webhook/*", flow: ["provider", "listener"] },
      { id: "webhook", label: "process-webhook", sub: "status → updates Mongo", hot: true, flow: ["listener", "webhook_queue"] }] },
  xpush: { x: 572, y: 470, w: 268, h: 252, name: "xPush", node: "xpush_api",
    rows: [
      { id: "api", label: "xpush-api", sub: "HTTP /send → messageQue", flow: ["xpush_api", "xpush_redis"] },
      { id: "procmsg", label: "process-msg", sub: "→ serverMap pub/sub", hot: true, flow: ["xpush_redis", "socket_server"] },
      { id: "register", label: "xpush-register", sub: "device tokens → serverMap", flow: null }] },
  socket: { x: 1212, y: 470, w: 252, h: 124, name: "Socket Server", node: "socket_server",
    rows: [{ id: "socket", label: "socket-server", sub: "websocket push", flow: ["xpush_redis", "socket_server"] }] },
};
// ── Redis instances (ONE box each) → backend buffer node. ──────────────────────────────────────
const REDIS = {
  custIdn: { x: 18, y: 262, w: 236, h: 98, name: "CustIdn", sub: "Customer Lookup", node: "custidn_redis" },
  streamWriter: { x: 300, y: 118, w: 228, h: 96, name: "Stream Writer", sub: "Incoming Messages", node: "incoming_redis" },
  rightRoute: { x: 900, y: 176, w: 260, h: 108, name: "Right Route", sub: "Routing · Webhooks · Campaigns", node: "right_route_redis" },
  messageQue: { x: 252, y: 540, w: 228, h: 108, name: "MessageQue", sub: "xPush API Request", node: "xpush_redis" },
  serverMap: { x: 900, y: 560, w: 260, h: 104, name: "Server Map", sub: "device ↔ server", node: "servermap_redis" },
};
const STORES = { mongo: { x: 858, y: 362, w: 260, h: 96, name: "Mongo DB", node: "mongo" } };
const EXT = {
  providers: { x: 1212, y: 80, w: 252, h: 100, name: "Providers", sub: "SMS · WA · Email · Voice" },
  devices: { x: 1212, y: 648, w: 252, h: 100, name: "Devices", sub: "Push & In-App" },
};
// Edge id → backend flow pair for its rate (null = no backend rate → static line, no particles).
const EDGE_FLOW = {
  e1: ["incoming_api", "incoming_redis"], e2: ["incoming_redis", "right_route"], e3: ["channel", "provider"],
  e4: ["right_route", "right_route_redis"], e5: ["campaign_engine", "campaign_queue"], e6: ["right_route_redis", "channel"], e7: ["right_route", "mongo"],
  d1: ["provider", "listener"], d2: ["listener", "webhook_queue"], d3: null, d4: ["webhook_queue", "mongo"],
  p0: ["channel", "xpush_api"], p1: ["xpush_api", "xpush_redis"], p2: null, p3: ["xpush_redis", "socket_server"],
  p4: null, p5: null, p6: ["socket_server", "inapp_client"], p7: ["xpush_redis", "fcm_apns"], x1: null, l1: null,
};

const rowCY = (m, id) => { const i = m.rows.findIndex((r) => r.id === id), rh = (m.h - HEADER_H) / m.rows.length; return m.y + HEADER_H + i * rh + rh / 2; };

function geom(sx) {
  const SX = (x) => x * sx;
  const any = (id) => MACHINES[id] || REDIS[id] || STORES[id] || EXT[id];
  const boxR = (id) => { const n = any(id); return [SX(n.x + n.w), n.y + n.h / 2]; };
  const boxL = (id) => { const n = any(id); return [SX(n.x), n.y + n.h / 2]; };
  const boxT = (id, fx = 0.5) => { const n = any(id); return [SX(n.x + n.w * fx), n.y]; };
  const boxB = (id, fx = 0.5) => { const n = any(id); return [SX(n.x + n.w * fx), n.y + n.h]; };
  const rowR = (id, row) => { const m = MACHINES[id]; return [SX(m.x + m.w), rowCY(m, row)]; };
  const rowL = (id, row) => { const m = MACHINES[id]; return [SX(m.x), rowCY(m, row)]; };
  return { SX, boxR, boxL, boxT, boxB, rowR, rowL };
}
function roundedPath(pts, r = 10) {
  if (pts.length < 3) return "M" + pts.map((p) => `${p[0]},${p[1]}`).join(" L");
  let d = `M${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const p0 = pts[i - 1], p1 = pts[i], p2 = pts[i + 1];
    const v1 = [p1[0] - p0[0], p1[1] - p0[1]], v2 = [p2[0] - p1[0], p2[1] - p1[1]];
    const l1 = Math.hypot(v1[0], v1[1]) || 1, l2 = Math.hypot(v2[0], v2[1]) || 1;
    const rr = Math.min(r, l1 / 2, l2 / 2);
    const a = [p1[0] - (v1[0] / l1) * rr, p1[1] - (v1[1] / l1) * rr];
    const b = [p1[0] + (v2[0] / l2) * rr, p1[1] + (v2[1] / l2) * rr];
    d += ` L${a[0]},${a[1]} Q${p1[0]},${p1[1]} ${b[0]},${b[1]}`;
  }
  const e = pts[pts.length - 1];
  return d + ` L${e[0]},${e[1]}`;
}

// Edge GEOMETRY only (rates are applied from the live flows at render time).
function buildEdges(sx) {
  const g = geom(sx), SX = g.SX, E = [];
  const add = (id, pts) => E.push({ id, d: roundedPath(pts) });
  let a, b;
  a = g.rowR("incoming", "accept"); b = g.boxL("streamWriter");
  add("e1", [a, [b[0], a[1]]]);
  a = g.boxR("streamWriter"); b = g.rowL("rrs", "dispatch");
  add("e2", [a, [SX(560), a[1]], [SX(560), b[1]], b]);
  a = g.rowR("rrs", "dispatch"); b = g.boxL("providers");
  add("e3", [[a[0], a[1] - 8], [SX(862), a[1] - 8], [SX(862), 118], [SX(1176), 118], [SX(1176), b[1]], b]);
  a = g.rowR("rrs", "validate");
  add("e4", [a, [SX(872), a[1]], [SX(872), 232], [SX(900), 232]]);
  a = g.rowR("rrs", "campaign");
  add("e5", [a, [SX(864), a[1]], [SX(864), 260], [SX(900), 260]]);
  a = g.rowR("rrs", "dispatch");
  add("e6", [[SX(900), 206], [SX(880), 206], [SX(880), a[1] + 8], [a[0], a[1] + 8]]);
  a = g.boxB("rrs", 0.86); b = g.boxL("mongo");
  add("e7", [a, [a[0], b[1]], b]);
  a = g.boxB("providers", 0.5); b = g.boxT("listener", 0.5);
  add("d1", [a, b]);
  a = g.rowL("listener", "listen");
  add("d2", [a, [SX(1194), a[1]], [SX(1194), 208], [SX(1160), 208]]);
  b = g.rowL("listener", "webhook");
  add("d3", [[SX(1160), 262], [SX(1178), 262], [SX(1178), b[1]], b]);
  a = g.rowL("listener", "webhook"); b = g.boxR("mongo");
  add("d4", [[a[0], a[1] + 8], [SX(1166), a[1] + 8], [SX(1166), b[1]], b]);
  a = g.boxB("rrs", 0.2); b = g.boxT("xpush", 0.2);
  add("p0", [a, b]);
  a = g.rowL("xpush", "api"); b = g.boxR("messageQue");
  add("p1", [a, [b[0], a[1]]]);
  a = g.boxR("messageQue"); b = g.rowL("xpush", "procmsg");
  add("p2", [[a[0], 606], [SX(524), 606], [SX(524), b[1]], b]);
  a = g.rowR("xpush", "procmsg"); b = g.boxL("socket");
  add("p3", [a, [SX(862), a[1]], [SX(862), 510], [SX(1174), 510], [SX(1174), b[1]], b]);
  a = g.rowR("xpush", "register"); b = g.boxB("serverMap", 0.5);
  add("p4", [a, [b[0], a[1]], b]);
  a = g.boxL("serverMap");
  add("p5", [[a[0], 588], [SX(880), 588], [SX(880), 636], [SX(840), 636]]);
  a = g.boxB("socket", 0.5); b = g.boxT("devices", 0.5);
  add("p6", [a, b]);
  a = g.boxB("xpush", 0.5); b = g.boxB("devices", 0.5);
  add("p7", [a, [a[0], 758], [b[0], 758], b]);
  a = g.boxR("devices"); b = g.rowR("listener", "listen");
  add("x1", [a, [SX(1474), a[1]], [SX(1474), b[1]], b]);
  a = g.boxT("custIdn", 0.5); b = g.boxB("incoming", 0.5);
  add("l1", [a, b]);
  return E;
}

// ── card renderers ─────────────────────────────────────────────────────────
function Bars({ cpu, ram, disk }) {
  const row = (lbl, v, c) => (
    <Flex align="center" gap="7px">
      <Text w="26px" fontSize="9px" color="gray.500">{lbl}</Text>
      <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden"><Box h="100%" w={`${v == null ? 0 : Math.min(100, v)}%`} bg={c(v)} transition="width .5s ease" /></Box>
      <Text w="32px" fontSize="10px" fontFamily="mono" fontWeight="700" textAlign="right" color={c(v)}>{v == null ? "—" : `${v}%`}</Text>
    </Flex>);
  return <Flex direction="column" gap="4px" mt="5px">{row("CPU", cpu, cpuC)}{row("RAM", ram, ramC)}{row("Disk", disk, ramC)}</Flex>;
}
function Machine({ m, bottleneckId, onOpen }) {
  const crit = m.status === "crit", wired = m.status !== "not_wired";
  const border = crit ? "red.200" : m.status === "warn" ? "orange.200" : "gray.200";
  const dot = crit ? "#DC2626" : m.status === "warn" ? "#EA580C" : wired ? "#16A34A" : "#94A3B8";
  const svcCount = (m.servers || []).reduce((s, b) => s + (b.services ? b.services.length : 0), 0);
  const clickable = svcCount > 0 && typeof onOpen === "function";
  return (
    <Flex direction="column" h="100%" borderRadius="lg" borderWidth="1px" borderColor={border} bg="white" boxShadow="0 1px 4px rgba(16,24,40,.07)" overflow="hidden" opacity={wired ? 1 : 0.65}
      cursor={clickable ? "pointer" : "default"} onClick={clickable ? () => onOpen(m) : undefined}
      _hover={clickable ? { borderColor: "blue.300", boxShadow: "0 0 0 2px rgba(109,124,255,.18)" } : undefined}
      title={clickable ? "Click for this box's per-service breakdown" : undefined} transition="border-color .15s, box-shadow .15s">
      <Box px="13px" pt="9px" pb="9px">
        <Flex align="center" gap="6px">
          <Box w="8px" h="8px" borderRadius="2px" bg={dot} flex="none" />
          <Text fontSize="13px" fontWeight="800" color="#111827" noOfLines={1} flex="1" minW="0">{m.name}</Text>
          {m.id === bottleneckId && <Badge colorScheme="red" variant="subtle" fontSize="7px" flex="none">Bottleneck</Badge>}
          {m.ip && <Text fontSize="7px" fontFamily="mono" color="gray.400" flex="none">{m.ip}</Text>}
        </Flex>
        {wired ? <Bars cpu={m.cpu} ram={m.ram} disk={m.disk} /> : <Text fontSize="9px" color="gray.400" mt="6px">no active monitor</Text>}
        {m.connections != null && (
          <HStack spacing={1} mt="5px" alignSelf="flex-start" bg="blue.50" borderRadius="full" px={2} py="1px" display="inline-flex">
            <Text fontSize="11px" fontFamily="mono" fontWeight="700" color="blue.600">{fmtInt(m.connections)}</Text>
            <Text fontSize="8px" color="blue.500">active conns</Text></HStack>
        )}
      </Box>
      <Flex direction="column" flex="1" borderTopWidth="1px" borderColor="gray.100">
        {m.rows.map((r, i) => (
          <Flex key={r.id} flex="1" align="center" gap="8px" px="13px" borderTopWidth={i ? "1px" : 0} borderColor="gray.100" bg={r.hot ? "#FFF3E6" : "white"}>
            <Box minW="0" flex="1"><Text fontSize="12px" fontFamily="mono" fontWeight="700" color="#111827" noOfLines={1}>{r.label}</Text>
              <Text fontSize="8.5px" color="gray.400" noOfLines={1}>{r.sub}</Text></Box>
            <Text fontSize="12px" fontFamily="mono" fontWeight="700" color={r.tps > 0 ? "#2563eb" : "gray.400"} flex="none">{fmtRate(r.tps)}</Text>
          </Flex>
        ))}
        {clickable && <Text fontSize="7.5px" color="gray.400" px="13px" pb="3px" alignSelf="flex-end">{svcCount} services ›</Text>}
      </Flex>
    </Flex>);
}
function Jar({ n, onOpen }) {
  const wired = n.status !== "not_wired" && n.status !== "muted" && n.mem != null;
  const pct = n.mem ?? 0, c = tone(n.mem, 90, 70);
  const clickable = (n.queues || []).length > 0 && typeof onOpen === "function";
  const cx = 26, rx = 20, ry = 5, topY = 8, botY = 42, liq = topY + (botY - topY) * (1 - pct / 100);
  const body = `M${cx - rx},${topY} L${cx - rx},${botY} A${rx},${ry} 0 0 0 ${cx + rx},${botY} L${cx + rx},${topY} A${rx},${ry} 0 0 1 ${cx - rx},${topY} Z`;
  const cid = `jar-${n.key}`;
  const L = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxW: "100%" };
  return (
    <Flex align="center" h="100%" gap="12px" px="14px" bg="white" borderRadius="lg" borderWidth="1px" borderColor={n.status === "crit" ? "red.200" : "gray.200"} boxShadow="0 1px 4px rgba(16,24,40,.06)" opacity={wired ? 1 : 0.65}
      cursor={clickable ? "pointer" : "default"} onClick={clickable ? () => onOpen(n) : undefined}
      _hover={clickable ? { borderColor: "blue.300", boxShadow: "0 0 0 2px rgba(109,124,255,.18)" } : undefined}
      title={clickable ? "Click for the BullMQ queues on this Redis" : undefined} transition="border-color .15s, box-shadow .15s">
      <Flex direction="column" align="center" flex="none" w="46px">
        <Box as="svg" width="46px" height="40px" viewBox="0 0 52 50" style={{ display: "block", overflow: "visible" }}>
          <defs><clipPath id={cid}><path d={body} /></clipPath></defs>
          <path d={body} fill="#f6f7f9" />
          {n.mem > 0 && <g clipPath={`url(#${cid})`}><rect x={cx - rx} y={liq} width={rx * 2} height={botY + ry - liq} fill={c} opacity="0.5" /><ellipse cx={cx} cy={liq} rx={rx} ry={ry} fill={c} opacity="0.85" /></g>}
          <path d={body} fill="none" stroke={c} strokeWidth="1.6" />
          <ellipse cx={cx} cy={topY} rx={rx} ry={ry} fill="white" stroke={c} strokeWidth="1.6" />
        </Box>
        <Text fontSize="11px" fontFamily="mono" fontWeight="700" color={c} mt="1px">{n.mem == null ? "—" : `${n.mem}%`}</Text>
      </Flex>
      <Box flex="1" minW="0">
        <Flex align="center" gap="5px"><Text fontSize="14px" fontWeight="800" color="#111827" {...L}>{n.name}</Text>
          {clickable && <Badge colorScheme="purple" variant="subtle" fontSize="6.5px" px="3px" flex="none">{n.queues.length} q</Badge>}</Flex>
        <Text fontSize="9.5px" color="gray.400" {...L}>{n.sub}</Text>
        {wired ? (
          <>
            <Text fontSize="11px" color="gray.800" mt="4px" {...L}><Text as="span" color="gray.500">Used: </Text><b>{fmtMB(n.used)}</b> / {fmtMB(n.free)}</Text>
            <Text fontSize="11px" fontFamily="mono" mt="1px" {...L}><Text as="span" color="#16A34A" fontWeight="700">r: {n.r ?? "—"}/s</Text><Text as="span" color="#DC2626" fontWeight="700" ml="10px">w: {n.wr ?? "—"}/s</Text></Text>
          </>
        ) : <Text fontSize="10px" color="gray.400" mt="6px">no active monitor</Text>}
      </Box>
    </Flex>);
}
function Store({ n }) {
  const cell = (lbl, v, acc) => (<Box flex="1" minW="0" textAlign="center" px="3px" borderLeftWidth="1px" borderColor="gray.100" _first={{ borderLeftWidth: 0 }}>
    <Text fontSize="15px" fontFamily="mono" fontWeight="800" color={acc || "#111827"} lineHeight="1.05" noOfLines={1}>{v}</Text>
    <Text fontSize="8px" color="gray.400" textTransform="uppercase" letterSpacing="0.05em" noOfLines={1}>{lbl}</Text></Box>);
  const wired = n.status !== "not_wired";
  const dot = n.status === "crit" ? "#DC2626" : n.status === "warn" ? "#EA580C" : wired ? "#16A34A" : "#94A3B8";
  return (
    <Flex direction="column" h="100%" px="14px" py="10px" borderRadius="lg" borderWidth="1px" borderColor="gray.200" bg="white" boxShadow="0 1px 4px rgba(16,24,40,.06)" opacity={wired ? 1 : 0.65}>
      <Flex align="center" gap="7px" mb="2px"><Box w="9px" h="9px" borderRadius="3px" bg={dot} flex="none" /><Text fontSize="14px" fontWeight="800" color="#111827">{n.name}</Text>
        <Text fontSize="8px" color="gray.400" ml="auto" noOfLines={1}>tbl_message_logs</Text></Flex>
      {wired ? (
        <Flex mt="auto" align="stretch">{cell("Active", n.active == null ? "—" : fmtInt(n.active), "#2563eb")}{cell("Open", n.open == null ? "—" : fmtInt(n.open))}{cell("Memory", n.mem == null ? "—" : <>{Math.round(n.mem)}<Text as="span" fontSize="9px" color="gray.400">mb</Text></>)}{cell("R-W /s", n.rw == null ? "—" : n.rw)}</Flex>
      ) : <Text fontSize="10px" color="gray.400" mt="auto">no active monitor</Text>}
    </Flex>);
}
const Ext = ({ n }) => (
  <Flex direction="column" align="center" justify="center" h="100%" borderRadius="lg" bg="#e7eaf0" borderWidth="1px" borderColor="#d7dce4">
    <Text fontSize="15px" fontWeight="800" color="#374151">{n.name}</Text><Text fontSize="10px" color="gray.500" mt="1px">{n.sub}</Text></Flex>);

// ── drill-down modal (machine → per-service breakdown ; redis → BullMQ queues) ──
function fmtUptime(s) {
  if (s == null) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
}
function DetailModal({ node, onClose }) {
  const isRedis = node?.kind === "redis";
  const servers = node?.servers || [], queues = node?.queues || [];
  return (
    <Modal isOpen={!!node} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="16px">{node?.name}{isRedis ? " — BullMQ queues" : " — box breakdown"}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={5}>
          {isRedis ? (
            queues.length === 0 ? <Text fontSize="13px" color="gray.500">No BullMQ queues discovered on this Redis.</Text> : (
              <Box overflowX="auto"><Table size="sm" variant="simple">
                <Thead><Tr><Th fontSize="9px">Queue</Th><Th fontSize="9px" isNumeric>Waiting</Th><Th fontSize="9px" isNumeric>Active</Th><Th fontSize="9px" isNumeric>Delayed</Th><Th fontSize="9px" isNumeric>Failed</Th><Th fontSize="9px" isNumeric>Depth</Th></Tr></Thead>
                <Tbody>{queues.slice().sort((a, b) => (b.depth || 0) - (a.depth || 0)).map((q, i) => (
                  <Tr key={q.queue || i}>
                    <Td fontSize="11px" fontFamily="mono" fontWeight="600">{q.queue}</Td>
                    <Td fontSize="11px" fontFamily="mono" isNumeric color={q.waiting > 0 ? TONE.warn.fg : "gray.600"}>{fmtInt(q.waiting ?? 0)}</Td>
                    <Td fontSize="11px" fontFamily="mono" isNumeric color={q.active > 0 ? "blue.600" : "gray.600"}>{fmtInt(q.active ?? 0)}</Td>
                    <Td fontSize="11px" fontFamily="mono" isNumeric color="gray.500">{fmtInt(q.delayed ?? 0)}</Td>
                    <Td fontSize="11px" fontFamily="mono" isNumeric color={q.failed > 0 ? TONE.crit.fg : "gray.500"}>{fmtInt(q.failed ?? 0)}</Td>
                    <Td fontSize="11px" fontFamily="mono" isNumeric fontWeight="700" color={q.depth > 0 ? "#111827" : "gray.400"}>{fmtInt(q.depth ?? 0)}</Td>
                  </Tr>))}</Tbody>
              </Table></Box>
            )
          ) : servers.length === 0 ? <Text fontSize="13px" color="gray.500">No active monitor for this box.</Text> : servers.map((b, bi) => {
            const util = [["CPU", b.cpu_pct, cpuC], ["RAM", b.ram_pct, ramC], ["Disk", b.disk_pct, ramC]].filter((x) => x[1] != null);
            return (
              <Box key={b.node_name || bi} mb={bi < servers.length - 1 ? 5 : 0}>
                <Flex justify="space-between" align="baseline" wrap="wrap" gap={2}><Text fontSize="13px" fontWeight="700" color="#111827" fontFamily="mono">{b.node_name}</Text>
                  <Text fontSize="10px" color="gray.400">{b.cores != null ? `${b.cores} cores` : ""}{b.ram_total_mb != null ? ` · ${(b.ram_total_mb / 1024).toFixed(1)} GB RAM` : ""}{b.process_count != null ? ` · ${b.process_count} services` : ""}</Text></Flex>
                <HStack spacing={4} mt={2} mb={3} wrap="wrap">{util.map(([lbl, v, c]) => (
                  <HStack key={lbl} spacing={1.5}><Box w="8px" h="8px" borderRadius="full" bg={c(v)} /><Text fontSize="11px" color="gray.500">{lbl}</Text><Text fontSize="12px" fontFamily="mono" fontWeight="700" color={c(v)}>{v}%</Text></HStack>))}</HStack>
                <Box overflowX="auto"><Table size="sm" variant="simple">
                  <Thead><Tr><Th fontSize="9px">Service</Th><Th fontSize="9px">Status</Th><Th fontSize="9px" isNumeric>CPU</Th><Th fontSize="9px" isNumeric>Mem</Th><Th fontSize="9px" isNumeric>Restarts</Th><Th fontSize="9px" isNumeric>Uptime</Th></Tr></Thead>
                  <Tbody>{(b.services || []).map((s, si) => (
                    <Tr key={s.name || si}>
                      <Td fontSize="11px" fontFamily="mono" fontWeight="600">{s.name}</Td>
                      <Td><Badge colorScheme={s.status === "online" ? "green" : "red"} variant="subtle" fontSize="8px">{s.status || "—"}</Badge></Td>
                      <Td fontSize="11px" fontFamily="mono" isNumeric color={s.cpu_pct >= 60 ? TONE.warn.fg : "gray.700"}>{s.cpu_pct != null ? `${s.cpu_pct}%` : "—"}</Td>
                      <Td fontSize="11px" fontFamily="mono" isNumeric>{s.mem_mb != null ? `${s.mem_mb} MB` : "—"}</Td>
                      <Td fontSize="11px" fontFamily="mono" isNumeric color={s.restarts > 5 ? TONE.warn.fg : "gray.600"}>{s.restarts ?? "—"}</Td>
                      <Td fontSize="11px" fontFamily="mono" isNumeric color="gray.600">{fmtUptime(s.uptime_s)}</Td>
                    </Tr>))}</Tbody>
                </Table></Box>
                {bi < servers.length - 1 && <Divider mt={4} />}
              </Box>);
          })}
        </ModalBody>
      </ModalContent>
    </Modal>);
}

// ── bottleneck verdict banner ──
function Verdict({ bn }) {
  const healthy = bn.healthy, t = TONE[healthy ? "ok" : "crit"], ev = bn.evidence || {};
  const chips = [];
  if (ev.gap_per_min != null) chips.push(["gap", `${ev.gap_per_min > 0 ? "+" : ""}${fmtRate(ev.gap_per_min / 60)}`, ev.gap_per_min > 0]);
  if (ev.backlog != null) chips.push(["backlog", fmtInt(ev.backlog), !healthy]);
  if (ev.rr_cpu_pct != null) chips.push(["RR CPU", `${ev.rr_cpu_pct}%`, ev.rr_cpu_pct >= 80]);
  if (ev.in_cpu_pct != null) chips.push(["Incoming CPU", `${ev.in_cpu_pct}%`, ev.in_cpu_pct >= 80]);
  if (ev.delivery_pct != null) chips.push(["delivered", `${ev.delivery_pct}%`, ev.delivery_pct < 85]);
  const stageName = { incoming_api: "Incoming API", right_route: "Right Route Server", channel: "send-message-job", listener: "Webhook Listener", xpush_api: "xPush" }[bn.stage] || bn.stage;
  return (
    <Flex align="center" gap={3} wrap="wrap" mb={3} px={3} py="7px" borderRadius="lg" bg={healthy ? TONE.ok.bg : TONE.crit.bg} borderWidth="1px" borderColor={healthy ? "green.200" : "red.200"} borderLeftWidth="3px" borderLeftColor={t.dot}>
      <Icon as={healthy ? CheckCircleIcon : WarningTwoIcon} color={t.dot} boxSize="15px" flex="none" />
      <Text fontSize="12.5px" fontWeight="700" color="#111827" noOfLines={1} minW="0">
        {healthy ? <><Text as="span" color={t.fg}>Pipeline healthy</Text> · inflow ≈ outflow — buffers stable</>
          : <><Text as="span" color={t.fg}>Bottleneck · {stageName}</Text> — {bn.reason}</>}
      </Text>
      <HStack spacing="6px" wrap="wrap" ml="auto">{chips.map(([k, v, hot]) => (
        <HStack key={k} spacing={1} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="6px" px="6px" py="1px">
          <Box w="5px" h="5px" borderRadius="full" bg={hot ? TONE.crit.dot : TONE.ok.dot} /><Text fontSize="9px" color="gray.500">{k}</Text>
          <Text fontSize="10px" fontFamily="mono" fontWeight="700" color={hot ? TONE.crit.fg : "#111827"}>{v}</Text></HStack>))}</HStack>
    </Flex>);
}

// Map the live pipeline payload → render-ready boxes (metrics intact; missing node → not_wired).
function useModel(pipeline) {
  return useMemo(() => {
    const byId = Object.fromEntries((pipeline?.nodes || []).map((n) => [n.id, n]));
    const flow = (pair) => (pair ? (pipeline?.flows || []).find((f) => f.from === pair[0] && f.to === pair[1]) : null);
    const machines = Object.entries(MACHINES).map(([key, m]) => {
      const n = byId[m.node];
      return { ...m, id: key, status: n?.status || "not_wired", cpu: n?.cpu_pct ?? null, ram: n?.ram_pct ?? null, disk: n?.disk_pct ?? null,
        connections: n?.connections ?? null, servers: n?.servers || [],
        rows: m.rows.map((r) => ({ ...r, tps: flow(r.flow)?.tps ?? null })) };
    });
    const redis = Object.entries(REDIS).map(([key, r]) => {
      const n = byId[r.node];
      return { ...r, key, status: n?.status || "not_wired", mem: n?.mem_pct ?? null, used: n?.mem_used_mb ?? null, free: n?.mem_free_mb ?? null,
        r: n?.reads_per_sec ?? null, wr: n?.writes_per_sec ?? null, queues: n?.queues || [] };
    });
    const mn = byId.mongo;
    const stores = [{ ...STORES.mongo, key: "mongo", kind: "store", status: mn?.status || "not_wired", active: mn?.connections_active ?? null, open: mn?.connections ?? null, mem: mn?.resident_mb ?? null, rw: mn?.reads_per_sec != null ? `${mn.reads_per_sec}·${mn.writes_per_sec ?? "—"}` : null }];
    const edgeTps = Object.fromEntries(Object.keys(EDGE_FLOW).map((id) => [id, flow(EDGE_FLOW[id])?.tps ?? null]));
    const edgeCong = Object.fromEntries(Object.keys(EDGE_FLOW).map((id) => [id, !!flow(EDGE_FLOW[id])?.congested]));
    return { machines, redis, stores, edgeTps, edgeCong };
  }, [pipeline]);
}

export default function PipelineFlow({ pipeline }) {
  const wrapRef = useRef(null);
  const [cw, setCw] = useState(BASE_W);
  const [detail, setDetail] = useState(null); // clicked machine/redis → drill-down modal
  useEffect(() => {
    const el = wrapRef.current; if (!el || typeof ResizeObserver === "undefined") return;
    const measure = () => setCw(Math.max(980, el.clientWidth));
    measure(); const ro = new ResizeObserver(measure); ro.observe(el); return () => ro.disconnect();
  }, []);
  const sx = cw / BASE_W;
  const edges = useMemo(() => buildEdges(sx), [sx]);
  const { machines, redis, stores, edgeTps, edgeCong } = useModel(pipeline);
  const bn = pipeline?.bottleneck;
  const bottleneckId = bn && !bn.healthy
    ? ({ right_route: "rrs", channel: "rrs", incoming_api: "incoming", listener: "listener", xpush_api: "xpush", socket_server: "socket" }[bn.stage] || null)
    : null;
  const dur = (tps) => (!tps ? 6.5 : Math.max(1.4, Math.min(4.5, 60 / tps)));
  const place = (n) => ({ position: "absolute", left: `${n.x * sx}px`, top: `${n.y}px`, width: `${n.w * sx}px`, height: `${n.h}px` });

  return (
    <Box mb={5}>
      {bn && <Verdict bn={bn} />}
      <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" p={3}>
        <Flex justify="space-between" mb={3}>
          <Text fontSize="11px" color="gray.500" fontWeight="700" letterSpacing="0.12em" textTransform="uppercase">Message lifecycle · infrastructure</Text>
          <Text fontSize="10px" color="gray.400">particles = messages in flight · speed ∝ throughput</Text>
        </Flex>
        <Box ref={wrapRef}>
          <Box position="relative" w={`${cw}px`} h={`${H}px`}>
            <Box as="svg" viewBox={`0 0 ${cw} ${H}`} position="absolute" inset={0} w="100%" h="100%" style={{ overflow: "visible" }}>
              {edges.map((e) => {
                const tps = edgeTps[e.id], cong = edgeCong[e.id], hasFlow = tps > 0, d = dur(tps);
                const stroke = cong ? TONE.crit.dot : "#dde1e9", col = cong ? TONE.crit.dot : FLOW;
                return (
                  <g key={e.id}>
                    <path id={`mp-${e.id}`} d={e.d} fill="none" stroke={stroke} strokeWidth={cong ? 3 : 2.2} strokeLinecap="round" strokeLinejoin="round" />
                    {hasFlow && [0, 1, 2].map((i) => (
                      <circle key={i} r={cong ? 4 : 3.4} fill={col} opacity="0.95"><animateMotion dur={`${d}s`} begin={`${(i * d) / 3}s`} repeatCount="indefinite"><mpath href={`#mp-${e.id}`} /></animateMotion></circle>
                    ))}
                  </g>);
              })}
            </Box>
            {machines.map((m) => <Box key={m.id} style={place(m)}><Machine m={m} bottleneckId={bottleneckId} onOpen={setDetail} /></Box>)}
            {redis.map((n) => <Box key={n.key} style={place(n)}><Jar n={{ ...n, kind: "redis" }} onOpen={setDetail} /></Box>)}
            {stores.map((n) => <Box key={n.key} style={place(n)}><Store n={n} /></Box>)}
            {Object.entries(EXT).map(([k, n]) => <Box key={k} style={place(n)}><Ext n={n} /></Box>)}
          </Box>
        </Box>
      </Box>
      <DetailModal node={detail} onClose={() => setDetail(null)} />
    </Box>);
}
